/**
 * Full-mode parametric bootstrap MSE (brief 9.12, MET-11), implemented
 * behind parametricBootstrapMse in engine.ts.
 *
 * Procedure (brief 9.12), for one model cell and replicates b = 1..B:
 * fit the cell once, then per replicate draw independent
 *   v*_i ~ N(0, sigma2_v)        for every area, and
 *   e*_i ~ N(0, psi_i)           for every in-sample area,
 * form the true values theta*_i = x_i' betahat + v*_i and
 * p*_i = sin(clamp(theta*_i))^2, set the bootstrap direct estimates
 * thetahat*_i = theta*_i + e*_i (not clamped), and rerun the complete
 * cell procedure — the same fit (same covariates, estimation method,
 * and fallback rules) and the same back-transformation — on thetahat*.
 * The stored errors are thetatilde*_i - theta*_i on the transformed
 * scale and ptilde*_i - p*_i on the proportion scale, and
 *   mse_theta_i = mean_b (thetatilde*_i - theta*_i)^2,
 *   mse_p_i     = mean_b (ptilde*_i - p*_i)^2.
 * No bias adjustment is applied to either mean: the brief defines the
 * MSEs as the plain means of the squared errors, and the bias handling
 * of the pipeline lives inside the estimator being bootstrapped (the
 * bias-corrected back-transformation of brief 9.10, the engine's
 * default 'bc' method, is what produces ptilde*_i).
 *
 * Out-of-sample areas take part with their synthetic estimator: they
 * have no psi_i and draw no e*_i, their true values are generated
 * like every other area's, and their replicate estimate is the refit's
 * synthetic estimate. Their errors are defined exactly as above.
 *
 * Reproducibility (brief 9.12, VAL-03): each replicate draws from its
 * own stream derived from (seed, b) — see prng.ts — so a replicate's
 * values never depend on which worker computed it or where the chunk
 * boundaries fell. Draws are consumed in a fixed order (canonical
 * area order; per area first v*_i, then e*_i when the area is in
 * sample). The context sorts the domains by areaId on entry
 * (directive C-1 companion fix): the draw assignment — and therefore
 * the result, bit for bit — is then independent of the order the
 * caller supplied the areas in, which the VAL-03 row-order
 * invariance gate requires now that adjusted-REML cells bootstrap
 * in every mode. Squared errors are kept per replicate and reduced
 * in ascending replicate order, so the floating-point summation
 * order is the same for every chunking. createBootstrapContext /
 * bootstrapReplicateErrors / assembleBootstrapResult are the seams a
 * worker host uses to split the replicate range;
 * parametricBootstrapMseImpl is the single-range composition of the
 * three.
 *
 * Benchmarking (brief 9.13, resolved in Phase 1 — see
 * validation/parity/OPEN_QUESTIONS.md Q14): when external
 * benchmarking is enabled, the options must also carry the area
 * population counts (the weights omega_i); the function refuses an
 * enabled external configuration without them, loudly, rather than
 * silently returning unbenchmarked MSEs. Each replicate then
 * benchmarks its own estimates to a replicate target.
 *
 * The replicate target is the FAITHFUL construction (study RB-5,
 * Decision Log Addenda 20-22; the Q14 own-directs reading is
 * superseded): the target is drawn around the replicate's TRUE
 * population-weighted aggregate sum_i omega_i p*_i (weights
 * renormalized over in-sample areas, exactly as the production C-5
 * target is) with the direct target's sampling variance
 *   Var(T) = sum_i omega_i^2 sin^2(2 theta*_i) psi_i
 *            / (sum_i omega_i)^2
 * (delta method on the arcsine scale: d sin^2(theta)/d theta =
 * sin(2 theta); on the linear scale the derivative is 1 and psi_i
 * enters directly). One extra normal draw per replicate, taken
 * after the per-area draws. The Phase 1 construction — the
 * population-weighted mean of the replicate's OWN direct
 * estimates — is displaced from the replicate's true aggregate on
 * the transformed scale by about psi_i x (1 - 2 p_i) per area (the
 * delta-method/Jensen term): at flagship sample sizes about 1.8
 * points, which study RB-5 measured flattering the shipped
 * default's flagship coverage by about 6 points (0.930 shipped
 * target vs 0.870 faithful). A real benchmark target, formed from
 * direct estimates on the proportion scale, is nearly unbiased for
 * the population aggregate with the sampling variance above, so
 * the faithful target reproduces the production target's actual
 * relationship to the truth; the own-directs target reproduced
 * its letter and misstated its behavior. The production
 * point-estimate benchmarking against the real statewide direct
 * estimate does not change; only the target the bootstrap's
 * replicates aim at changed. The observed-data target
 * (options.benchmarkTarget) is carried for the record; it is not
 * used inside replicates.
 *
 * Study-only variant (Tier 2 Item 9 arm O; verdict 3.3): setting
 * options.replicateTarget to 'own-directs-bias-corrected' replaces
 * the faithful target with the Phase 1 own-directs construction —
 * the population-weighted mean of the replicate's own direct
 * proportions, which preserves the covariance between the target's
 * sampling error and the replicate's area errors that the faithful
 * draw structurally drops — with each replicate proportion's
 * first-order bias removed: p-hat*_i is replaced by
 * p-hat*_i - psi_i x (1 - 2 p*_i) before weighting. The bias term
 * is the delta-method first-order bias of sin^2(theta-hat*) as an
 * estimator of sin^2(theta*): with g(theta) = sin^2(theta),
 * g''(theta) = 2 cos(2 theta), so E[g(theta-hat*)] is approximately
 * g(theta*) + psi x cos(2 theta*) = sin^2(theta*) + psi (1 - 2 p*)
 * — the displacement study RB-5 recorded for the shipped target.
 * The term is evaluated at the replicate truth theta*_i (known
 * inside the bootstrap), so the corrected target is first-order
 * unbiased for the replicate's true aggregate and the correction —
 * constant given the replicate's truths — leaves the target's
 * sampling variance and its covariance with the area draws to the
 * own-directs part alone. On the linear scale g is the identity,
 * the bias term is zero, and the variant is the plain own-directs
 * target. The variant consumes no stream draw. It exists only
 * behind this option; every production caller leaves it absent.
 *
 * Under benchmarking, the transformed-scale
 * error is measured on the benchmarked estimate's theta,
 * asin(sqrt(ptilde_bench*_i)), matching the quantity the Full-mode
 * interval of brief 9.13 is built around. On the linear scale every
 * arcsine map in this paragraph is the identity (theta and p are
 * the same quantity; OPEN_QUESTIONS Q44). Internal consistency
 * benchmarking is refused: it acts across subgroup cells, and a
 * single-cell bootstrap has no other cells to be consistent with.
 */
import { backTransformAreasImpl } from './backtransform.js';
import { benchmarkEstimates } from './benchmarking.js';
import { arcsineExactExpectationCorrection, arcsineMeanBiasCorrection, } from './transform.js';
import { fitFayHerriotImpl } from './fayherriot.js';
import { createReplicateStream, nextGamma } from './prng.js';
/** Brief 9.12: the minimum replicate count (the default is 500). */
export const MIN_BOOTSTRAP_REPLICATES = 200;
const HALF_PI = Math.PI / 2;
function clampTheta(t) {
    return Math.min(Math.max(t, 0), HALF_PI);
}
export function createBootstrapContext(suppliedDomains, options) {
    // Canonical area order (see the header's reproducibility note):
    // every downstream consumer — the initial fit, the per-replicate
    // draw consumption, the assembled result — works on this ordering,
    // so results are independent of the caller's area order.
    const domains = [...suppliedDomains].sort((a, b) => a.areaId < b.areaId ? -1 : a.areaId > b.areaId ? 1 : 0);
    const { replicates, benchmarking } = options;
    if (!Number.isInteger(replicates) || replicates < MIN_BOOTSTRAP_REPLICATES) {
        throw new RangeError(`parametricBootstrapMse: replicates must be an integer of at least ` +
            `${MIN_BOOTSTRAP_REPLICATES} (brief 9.12); received ${replicates}.`);
    }
    if (benchmarking.internal) {
        throw new Error('parametricBootstrapMse: internal consistency benchmarking acts ' +
            'across subgroup cells and cannot be applied inside a ' +
            'single-cell bootstrap (brief 9.13). Run Full mode with ' +
            'internal benchmarking off; see validation/parity/' +
            'OPEN_QUESTIONS.md Q14.');
    }
    let benchmarkOmegas = null;
    if (benchmarking.external !== 'none') {
        const counts = options.populationCounts;
        if (counts === undefined) {
            throw new Error('parametricBootstrapMse: external benchmarking is enabled but ' +
                'no population counts were supplied, so the benchmarking ' +
                'weights cannot be formed and benchmarking cannot be applied ' +
                'inside the replicates (brief 9.13). Supply populationCounts ' +
                'in the bootstrap options; see validation/parity/' +
                'OPEN_QUESTIONS.md Q14.');
        }
        const raw = domains.map((d) => {
            const n = counts[d.areaId];
            if (n === undefined || !Number.isFinite(n) || n <= 0) {
                throw new Error(`parametricBootstrapMse: no usable population count for area ` +
                    `"${d.areaId}" — benchmarking inside replicates requires a ` +
                    'count for every area in the cell (brief 9.13).');
            }
            return n;
        });
        const total = raw.reduce((a, b) => a + b, 0);
        benchmarkOmegas = raw.map((n) => n / total);
    }
    const fit = fitFayHerriotImpl(domains, options.fitConfig);
    return {
        domains,
        options,
        sigma2v: fit.sigma2v,
        syntheticTheta: fit.areas.map((a) => a.syntheticTheta),
        benchmarkOmegas,
        ...(fit.robustNu !== undefined && Number.isFinite(fit.robustNu)
            ? { robustNu: fit.robustNu }
            : {}),
    };
}
export function bootstrapReplicateErrors(context, start, end) {
    const { domains, options } = context;
    const m = domains.length;
    if (!Number.isInteger(start) ||
        !Number.isInteger(end) ||
        start < 0 ||
        end < start ||
        end > options.replicates) {
        throw new RangeError(`bootstrapReplicateErrors: invalid replicate range [${start}, ${end}) ` +
            `for B = ${options.replicates}.`);
    }
    const thetaSq = new Float64Array((end - start) * m);
    const pSq = new Float64Array((end - start) * m);
    const sdV = Math.sqrt(context.sigma2v);
    const linear = options.transform === 'linear';
    // The proportion-scale image of a theta value as a direct estimate
    // (used for the mean-bias correction's plug-in proportion):
    // sin^2 on the arcsine scale — in [0, 1] by construction — and
    // the range-clamped identity on the linear scale, where a
    // generated direct can leave [0, 1] (Q44).
    const directProportion = (t) => linear
        ? Math.min(1, Math.max(0, t))
        : Math.sin(clampTheta(t)) ** 2;
    for (let b = start; b < end; b++) {
        const stream = createReplicateStream(options.seed, b);
        const thetaStar = new Array(m);
        const pStar = new Array(m);
        // The raw replicate direct estimate, before the mean-bias
        // correction mirror is applied: the own-directs replicate
        // target (study-only variant) is formed from it, exactly as
        // the Phase 1 construction was. Capturing it changes no
        // existing computation.
        const thetaHatRaw = new Array(m);
        const bootDomains = new Array(m);
        for (let i = 0; i < m; i++) {
            const d = domains[i];
            // Replicate effect: the shipped normal draw, or — when the
            // context's fit was by the F-2 class at a finite nu — the
            // fitted Student-t draw, a normal scale mixture
            // v* = sqrt(sigma2v / w*) z with w* ~ Gamma(nu/2, nu/2) from
            // the same replicate stream (registered in the F-2 study file).
            const vStar = context.robustNu !== undefined
                ? (sdV / Math.sqrt(nextGamma(stream, context.robustNu / 2) / (context.robustNu / 2))) *
                    stream.nextNormal()
                : sdV * stream.nextNormal();
            const tStar = context.syntheticTheta[i] + vStar;
            thetaStar[i] = tStar;
            // The replicate truth on the proportion scale: the exact image
            // of the theta truth — sin^2 for arcsine fits, theta* itself
            // for linear fits, whose generated values the normal model can
            // place outside [0, 1] just as the fitted scale allows (Q44).
            pStar[i] = linear ? tStar : Math.sin(clampTheta(tStar)) ** 2;
            if (d.outOfSample) {
                bootDomains[i] = d;
                thetaHatRaw[i] = d.thetaHat;
            }
            else {
                const eStar = Math.sqrt(d.psi) * stream.nextNormal();
                let thetaHatStar = tStar + eStar;
                thetaHatRaw[i] = thetaHatStar;
                if (options.meanBiasCorrection === true && !linear && d.psi > 0) {
                    thetaHatStar +=
                        options.meanBiasCorrectionForm === 'exact-expectation'
                            ? arcsineExactExpectationCorrection(directProportion(thetaHatStar), 1 / (4 * d.psi))
                            : arcsineMeanBiasCorrection(directProportion(thetaHatStar), 1 / (4 * d.psi));
                }
                bootDomains[i] = { ...d, thetaHat: thetaHatStar };
            }
        }
        const fitB = fitFayHerriotImpl(bootDomains, options.fitConfig);
        let btB = backTransformAreasImpl(fitB, {
            method: 'bc',
            confidence: options.confidence,
            transform: options.transform,
        });
        let thetaEstimates = fitB.areas.map((a) => a.eblupTheta);
        if (context.benchmarkOmegas !== null) {
            const omegas = context.benchmarkOmegas;
            let replicateTarget;
            if (options.replicateTarget === 'own-directs-bias-corrected') {
                // Study-only variant (Tier 2 Item 9 arm O; module header):
                // the own-directs target — the population-weighted mean of
                // the replicate's own direct proportions, weights
                // renormalized over in-sample areas — with each replicate
                // proportion's first-order bias removed:
                // sin^2(thetahat*_i) - psi_i x (1 - 2 p*_i), the
                // delta-method bias of sin^2(thetahat*) evaluated at the
                // replicate truth. On the linear scale the bias term is
                // zero and this is the plain own-directs target. No stream
                // draw is consumed, so every v* / e* draw of the replicate
                // is the same as the faithful arm's.
                let weightSum = 0;
                let weightedCorrected = 0;
                for (let i = 0; i < m; i++) {
                    if (domains[i].outOfSample)
                        continue;
                    weightSum += omegas[i];
                    const bias = linear ? 0 : domains[i].psi * (1 - 2 * pStar[i]);
                    weightedCorrected +=
                        omegas[i] * (directProportion(thetaHatRaw[i]) - bias);
                }
                replicateTarget = weightedCorrected / weightSum;
            }
            else {
                // Benchmarking inside the replicate (brief 9.13): the
                // target is the FAITHFUL replicate target (study RB-5;
                // module header) — drawn around the replicate's true
                // population-weighted aggregate with the direct target's
                // sampling variance, weights renormalized over in-sample
                // areas. The target draw is taken here, after the per-area
                // draws, so every v* / e* draw of the replicate is the same
                // as an unbenchmarked replicate's.
                let weightSum = 0;
                let weightedTruth = 0;
                let varSum = 0;
                for (let i = 0; i < m; i++) {
                    if (domains[i].outOfSample)
                        continue;
                    weightSum += omegas[i];
                    weightedTruth += omegas[i] * pStar[i];
                    // Delta-method variance of one area's direct proportion:
                    // sin^2(2 theta*) x psi on the arcsine scale (the map is
                    // sin^2), psi itself on the linear scale (identity map).
                    const deriv = linear ? 1 : Math.sin(2 * clampTheta(thetaStar[i]));
                    varSum += omegas[i] * omegas[i] * deriv * deriv * domains[i].psi;
                }
                const truthAggregate = weightedTruth / weightSum;
                const varTarget = varSum / (weightSum * weightSum);
                replicateTarget =
                    truthAggregate + Math.sqrt(varTarget) * stream.nextNormal();
            }
            btB = benchmarkEstimates(btB, omegas, options.benchmarking.external, replicateTarget, options.confidence, options.transform ?? 'arcsin');
            // The benchmarked estimate's theta: asin(sqrt(p)) on the
            // arcsine scale; the estimate itself on the linear scale,
            // where theta and p are the same quantity (Q44).
            thetaEstimates = linear
                ? btB.map((e) => e.estimate)
                : btB.map((e) => Math.asin(Math.sqrt(Math.min(1, Math.max(0, e.estimate)))));
        }
        if (options.publishedThetaError === true) {
            // Study-only (Decision 81, Candidate J): the transformed-scale
            // error is the published estimate's own scale image in every
            // world — btB already carries the bias-corrected
            // back-transformation and, when benchmarking inside the
            // replicates is on, the benchmarking shift above — so this
            // replaces whichever theta the shipped reductions selected.
            // No stream draw is consumed and pSq is untouched.
            thetaEstimates = linear
                ? btB.map((e) => e.estimate)
                : btB.map((e) => Math.asin(Math.sqrt(Math.min(1, Math.max(0, e.estimate)))));
        }
        const offset = (b - start) * m;
        for (let i = 0; i < m; i++) {
            const dTheta = thetaEstimates[i] - thetaStar[i];
            thetaSq[offset + i] = dTheta * dTheta;
            const dP = btB[i].estimate - pStar[i];
            pSq[offset + i] = dP * dP;
        }
    }
    return { thetaSq, pSq };
}
/**
 * Reduce complete per-replicate squared errors (all B replicates, in
 * ascending replicate order) to the bootstrap MSEs. The summation
 * order is fixed by replicate index, so any chunking that produced
 * the same per-replicate values assembles to the identical result.
 */
export function assembleBootstrapResult(context, thetaSq, pSq) {
    const { domains, options } = context;
    const m = domains.length;
    const B = options.replicates;
    if (thetaSq.length !== B * m || pSq.length !== B * m) {
        throw new Error(`assembleBootstrapResult: expected ${B * m} squared errors per scale ` +
            `(${B} replicates x ${m} areas); received ${thetaSq.length} and ${pSq.length}.`);
    }
    const areas = domains.map((d, i) => {
        let sumTheta = 0;
        let sumP = 0;
        for (let b = 0; b < B; b++) {
            sumTheta += thetaSq[b * m + i];
            sumP += pSq[b * m + i];
        }
        return { areaId: d.areaId, mseTheta: sumTheta / B, mseP: sumP / B };
    });
    return { replicates: B, seed: options.seed, areas };
}
/**
 * Full-mode parametric bootstrap MSE for one cell (brief 9.12,
 * MET-11): the single-range composition used by the public entry
 * point. Worker hosts split the work through createBootstrapContext
 * and bootstrapReplicateErrors and combine with
 * assembleBootstrapResult.
 */
export function parametricBootstrapMseImpl(domains, options) {
    const context = createBootstrapContext(domains, options);
    const { thetaSq, pSq } = bootstrapReplicateErrors(context, 0, options.replicates);
    return assembleBootstrapResult(context, thetaSq, pSq);
}
//# sourceMappingURL=bootstrap.js.map